import { ActKc3LevelEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { AddOrEditActKc3Modal } from 'pages/Project/UerProject/ActKC3List/components/AddOrEditActKc3/AddOrEditActKc3Modal';
import { WorkOrderStatusLabels } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { Button, ContextMenu, IconKebab, Tooltip } from 'uikit';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { ActListItemWithGroupInfo } from '../ActKC3ListTable/types';
import { ExportKc3Modal } from '../ExportKc3Modal';

interface IKebabActKC3 {
    contractId: number;
    onClose: (hasSaved?: boolean) => void;
    actKC3Id: number;
    isCorrection?: boolean;
    isEditable: boolean | null;
    isRemovable: boolean | null;
    isExportable: boolean | null;
    openSingleDelete: () => void;
    data?: ActListItemWithGroupInfo;
    actKC3Level: ActKc3LevelEnum;
    isOnlyDateEditable?: boolean;
    isFinal?: boolean;
}

export const KebabActKC3: React.FC<IKebabActKC3> = (props) => {
    const {
        actKC3Id,
        onClose,
        contractId,
        isCorrection,
        isEditable,
        isRemovable,
        isExportable,
        openSingleDelete,
        data,
        actKC3Level,
        isOnlyDateEditable,
        isFinal,
    } = props;
    const ref = useRef(null);
    const [isContextOpen, setIsContextOpen] = useState<boolean>(false);
    const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
    const [isExportOpen, setIsExportOpen] = useState<boolean>(false);

    const onContextMenuButtonClick = useCallback((evt) => {
        evt.stopPropagation();
        setIsContextOpen(R.not);
    }, []);

    const toggleExportModal = useCallback(() => {
        setIsExportOpen((p) => !p);
    }, []);

    const editTooltipData = useMemo(() => {
        if (data?.actKC3IsInactive) {
            return {};
        }

        if (actKC3Level === ActKc3LevelEnum.WorkOrder && data?.groupStatus !== WorkOrderStatusEnum.InProgress) {
            return {
                haveTooltip: true,
                tooltipName: 'woNotInProgress',
                tooltipMsg: `Редактирование недоступно. Наряд-Заказ переведен в статус «${
                    WorkOrderStatusLabels[data?.groupStatus as WorkOrderStatusEnum]
                }»`,
            };
        }

        if (!data?.isFinal && !isEditable) {
            return {
                haveTooltip: !data?.isFinal && !isEditable,
                tooltipName: 'notIsFinalKc3',
                tooltipMsg: 'Редактирование недоступно. Имеются последующие Справки КС-3',
            };
        }

        return {};
    }, [data, actKC3Level, isEditable]);

    const kebabMenuItems = useMemo(
        () => [
            {
                label: data?.actKC3IsInactive ? 'Просмотреть' : 'Редактировать',
                action: () => setIsEditOpen(true),
                disabled: !data?.actKC3IsInactive && !isEditable,
                ...editTooltipData,
            },
            {
                label: 'Выгрузить Справку КС-3',
                action: toggleExportModal,
                disabled: !isExportable,
            },
            {
                label: 'Удалить',
                action: openSingleDelete,
                disabled: !isRemovable,
            },
        ],
        [
            data?.actKC3IsInactive,
            editTooltipData,
            isEditable,
            isExportable,
            isRemovable,
            openSingleDelete,
            toggleExportModal,
        ],
    );

    const onClick = useCallback(
        (item) => () => {
            setIsContextOpen(false);
            const action = R.prop('action')(item) as () => void;

            if (action) {
                action();
            }
        },
        [],
    );

    const onModalClose = useCallback(
        (hasSaved?: boolean) => {
            setIsEditOpen(false);
            onClose(hasSaved);
        },
        [onClose],
    );

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    return (
        <>
            <Button
                onlyIcon
                iconLeft={IconKebab}
                size="xs"
                iconSize="s"
                view="clear"
                ref={ref}
                style={{ marginLeft: 'auto' }}
                onClick={onContextMenuButtonClick}
            />
            <ContextMenu
                size="s"
                isOpen={isContextOpen}
                offset={8}
                items={kebabMenuItems}
                getItemLabel={R.prop('label')}
                anchorRef={ref}
                direction="downStartLeft"
                getItemOnClick={onClick}
                onClickOutside={onContextMenuButtonClick}
                getItemAttributes={getMenuItemAttributes}
                onMouseLeave={onMenuMouseLeave}
                onMouseMove={onMenuMouseMove}
            />
            {isEditOpen && (
                <AddOrEditActKc3Modal
                    actKC3Id={actKC3Id}
                    isFinal={isFinal}
                    contractId={contractId}
                    onClose={onModalClose}
                    isReadonly={!isEditable}
                    isCorrection={isCorrection}
                    workOrderId={actKC3Level === ActKc3LevelEnum.WorkOrder ? Number(data?.groupKey) : undefined}
                    rootContractObjectId={actKC3Level === ActKc3LevelEnum.Object ? Number(data?.groupKey) : undefined}
                    isOnlyDateEditable={isOnlyDateEditable}
                />
            )}
            {isExportOpen && (
                <ExportKc3Modal actKC3Id={actKC3Id} workOrderId={data?.workOrderId} closeHandler={toggleExportModal} />
            )}
            {activeTooltipMsg && (
                <Tooltip
                    size="s"
                    direction="rightCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
        </>
    );
};

KebabActKC3.displayName = 'KebabActKC3';
